import styled from "styled-components";
import React, { RefObject, useEffect, useRef, useState } from 'react';
import { ThemeType } from "@src/types/theme";
import { t_library } from "@src/types/tables";
import DetailTitle from "./title/DetailTitle";
import DetailIcons from "./icons/DetailIcons";
import Image from "next/image";
import { RootState } from "@src/store";
import { useSelector } from "react-redux";
import DetailTags from "@src/layouts/detail/tags/DetailTags";
import DetailOthers from "@src/layouts/detail/others/DetailOthers";
import cssMixin from "@src/styles";
import Player from "@src/components/common/player/Player";
import { playerLib } from "@src/lib/player/player-lib";
import { getCookie } from "@src/lib/util/cookie";
import CartService from "@src/services/cart/CartService";
import UserService from "@src/services/user/UserService";
import { useRouter } from "next/router";
import Pagenation from "@src/components/common/pagenation/Pagenation";
import DetailIconsMobile from "./icons/DetailIconsMobile";
import { useWindowWidth } from "@src/hooks/util";

export type DetailContentsProps = {
    data: t_library;
    isPurchase: boolean;
    libraries: t_library[];
    listNum: number;
};

export default function DetailContents(props: DetailContentsProps)
{
    const router = useRouter();
    const media = useSelector((state: RootState) => state.media);
    const user = useSelector((state: RootState) => state.user);
    const imageWrapRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<HTMLDivElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
	const waveRef = useRef<HTMLDivElement>(null);
    const listArrayRef: RefObject<HTMLDivElement>[] = Array.from({ length: props.listNum }, () => useRef<HTMLDivElement>(null));
    const dataIdxRef = useRef<number>(-1);
    const windowWidth: number = useWindowWidth();
    
    const [ isMounted, setIsMounted ] = useState(false);
    const [ time, setTime ] = useState("");
    const [ fileId, setFileId ] = useState("");
    const [ imageHeight, setImageHeight ] = useState(0);
    const [ stateChange, setStateChange ] = useState<"" | "add" | "remove">("");
    const [ play, setPlay ] = useState(false);
	const [ playData, setPlayData ] = useState<t_library>();
    const [ playId, setPlayId ] = useState("");
	const [ musicUrl, setMusicUrl ] = useState("");
    const [ musicChange, setMusicChange ] = useState(false);
    const [ page, setPage ] = useState(0);
    const [ pageDelay, setPageDelay ] = useState(false);
    const [ buyList, setBuyList ] = useState<any[]>([]);
	const [ cartList, setCartList ] = useState<any[]>([]);

    function handleItemPlay(value: t_library)
    {
		setPlay(true);
		setPlayData(value);
	};

	function handleItemPause()
    {
		setPlay(false);
	};

    function handlePrevClick()
	{
		dataIdxRef.current = props.libraries.findIndex(e => e.id === playData!.id);

		if (dataIdxRef.current > 0)
		{
			let newData: t_library = props.libraries[dataIdxRef.current - 1];
			setPlayId(newData.id);

			if (process.browser)
			{
				let boundingRect = listArrayRef[dataIdxRef.current - 1].current?.getBoundingClientRect()!;
				playerLib.playPrev(dataIdxRef.current, newData, handleItemPlay, musicUrl, boundingRect);
			}
		}
		else if (page > 1)
		{
			setPage(page - 1);
			setPageDelay(true);
			dataIdxRef.current = props.listNum - 1;
		}
	};

	function handleNextClick()
	{
		dataIdxRef.current = props.libraries.findIndex(e => e.id === playData!.id);

		if (dataIdxRef.current < props.libraries.length - 1)
		{
			let newData: t_library | undefined = props.libraries[dataIdxRef.current + 1];

			if (process.browser)
			{
				let boundingRect = listArrayRef[dataIdxRef.current + 1].current?.getBoundingClientRect()!;
				let playerRect = playerRef.current?.getBoundingClientRect()!;
				
				playerLib.playNext(dataIdxRef.current, newData, handleItemPlay, musicUrl, boundingRect, playerRect);
			}
		}
		else if (page < Math.ceil(props.libraries.length / props.listNum))
		{
			setPage(page + 1);
			setPageDelay(true);
			dataIdxRef.current = 0;
		}
    };

    useEffect(() => {
        if (props.data)
            setFileId(props.isPurchase ? props.data.mp3FileId : props.data.sampleFileId)

        setTimeout(() => {
            let boundRect = imageWrapRef.current?.getBoundingClientRect()!;
            setImageHeight(boundRect?.height - 20);
        }, 10)
    }, [props.isPurchase, props.data]);

    useEffect(() => {
		if (isMounted)
		{
			router.push({
				pathname: '/page/[page]',
				query: {
					page: page,
					tags: router.query.tags
				},
			});
		}
		else
			setIsMounted(true);
	}, [page]);

    useEffect(() => {
		if (isMounted)
		{
			if (getCookie("accessToken"))
			{
				UserService.buyList({ userId: user.id }).then((res) => setBuyList(res));
				CartService.cartLibraries({ userId: user.id }).then((res) => setCartList(res));
			}
		}
	}, [isMounted]);

    useEffect(() => {
		if (!playData)
			return;

        const fileId = playData.sampleFileId;

        playerLib.loadFile(fileId, setMusicUrl);
		setMusicChange(true);
    }, [playData]);

    useEffect(() => {
		let audio = audioRef.current;
	
		if (audio)
		{
			if (play)
			{
				if (musicUrl)
					audio.play();
				else
				{
					audio!.onloadeddata = (event) => {
						audio!.play();
					};
				}
			}
			else
				audio.pause();
		}
	}, [play]);

    useEffect(() => {
		if (playId)
		{
			let audio = audioRef.current;
			audio!.pause();
		}
	}, [playId]);

    useEffect(() => {
        let boundRect = imageWrapRef.current?.getBoundingClientRect()!;
        setImageHeight(boundRect?.height - 20);
    }, [media, windowWidth]);

    return(
        <Wrapper>
            <MainImageWrap ref={ imageWrapRef }>
                { media.isDesktop && media.deviceLevel === "xlargeTablet" ?
                    <Image
                        src="/images/landingPage/Main.png"
                        fill
                        alt=""
                    />
                    :
                    <Image style={{ zIndex: -1 }}
                        src="/images/landingPage/Main.png"
                        fill
                        objectFit="cover"
                        alt=""
                    />
                }
                <InfoWrapper>
                    { props.data &&
                        <InfoMainWrap>
                            <DetailTitle data={ props.data } handleItemPlay={ handleItemPlay } handleItemPause={ handleItemPause } play={ play } playId={ playId } setPlayId={ setPlayId } />
                            { (media.isMobile || media.deviceLevel === "smallTablet" || media.deviceLevel === "tablet") &&
                                <DetailIconsMobile total={ time } library={ props.data } isPurchase={ props.isPurchase } />
                            }
                            <DetailTags data={ props.data } />
                        </InfoMainWrap>
                    }
                    { !(media.isMobile || media.deviceLevel === "smallTablet" || media.deviceLevel === "tablet") &&
                        <DetailIcons total={ time } library={ props.data } isPurchase={ props.isPurchase } />
                    }
                </InfoWrapper>
            </MainImageWrap>
            <DetailOthers
                height={ imageHeight - 80 }
                libraries={ props.libraries }
                handleItemPlay={ handleItemPlay }
                handleItemPause={ handleItemPause }
                play={ play }
                playId={ playId }
                setPlayId={ setPlayId }
                listArrayRef={ listArrayRef }
            />
            <Pagenation selectedPage={ page } setSelectedPage={ setPage } listNum={ props.listNum! } />
            { playData &&
                <Player
                    data={ playData }
                    playerRef={ playerRef }
                    srcUrl={ musicUrl }
                    waveRef={ waveRef }
                    audioRef={ audioRef }
                    play={ play }
                    setPlay={ setPlay }
                    musicChange={ musicChange }
                    setMusicChange={ setMusicChange }
                    isPurchase={ buyList && buyList.some(e => e && e.LIBRARY_USE_ID === playData.id) }
                    isInCart={ cartList && cartList.some(e => e && e.LIBRARY_ID === playData.id) }
                    setStateChange={ setStateChange }
                    handlePrevClick={ handlePrevClick }
                    handleNextClick={ handleNextClick }
                />
            }
        </Wrapper>
    );
}

const Wrapper = styled.div`
    width: 100%;
    margin: 0 auto 20px;
    padding: 80px;

    ${({ theme }: { theme: ThemeType }) => theme.largeTablet`
        padding: 80px 40px;
	`};

    ${({ theme }: { theme: ThemeType }) => theme.smallTablet`
		padding: 80px 20px;
	`};
`;

const MainImageWrap = styled.div`
	width: 100%;
    /* height: 100%; */
    margin: 0 auto;
    padding: 0 80px;
	position: absolute;
    top: 100px;
    left: 0;
    
    span {
        z-index: -1;
    };

    ${({ theme }: { theme: ThemeType }) => theme.largeTablet`
        top: 80px;
	`};

    ${({ theme }: { theme: ThemeType }) => theme.largeTablet`
        padding: 0 40px;
	`};

    ${({ theme }: { theme: ThemeType }) => theme.smallTablet`
		padding: 0 20px;
	`};
`;

const InfoWrapper = styled.div`
    ${ cssMixin.container };
    padding: 80px;
    z-index: 1;
    
    ${({ theme }: { theme: ThemeType }) => theme.labtopL`
		padding: 80px 20px;
	`};

    ${({ theme }: { theme: ThemeType }) => theme.largeTablet`
        padding: 50px 0;
	`};
`;

const InfoMainWrap = styled.div`
    display: flex;

    ${({ theme }: { theme: ThemeType }) => theme.largeTablet`
        display: initial;
	`};
`;